/* =========================================================================
   types.ts — Domain types used across the financial model.
   ========================================================================= */

import type { CensusArea } from "./census";
import type { DatedFinanceInput } from "./dated-finance-types";
import type { OwnershipInput } from "./dated-ownership";
import type { ComparisonPreset } from "./comparison-presets";

// Default CPI assumption used across the model, PDF narrative, SPA report, and tests.
export const DEFAULT_CPI_ASSUMPTION = 0.03;

// School construction-spec tier — selects which education $/m² band the construction
// benchmark reads from data/education_construction_costs_2021_2026.json (keys match the JSON rows).
export type SchoolSpecTier =
  "gov_primary" | "gov_secondary" | "private_mid" | "private_high";

export type CostAllowanceInput =
  { mode: "amount"; amount: number } | { mode: "rate"; rate: number };

export type ContributionTiming = "upfront" | "progressive" | "completion";

export interface LoadedDeveloperCostBasis {
  mode: "loaded_aggregate";
  // Preset identity; selects the scope register. Blobs saved before this field
  // existed are the Kurraba basis (undefined falls back to it).
  sourceId?: string;
  sourceName: string;
  sourceDate: string;
  buildingWorks: number;
  siteWorks: number;
  sourceBuildingAreaSqm: number;
  embeddedPreliminariesRate: number;
  embeddedBuilderMarginRate: number;
  embeddedBuilderOverheadsRate: number;
  upperBoundBuildingWorks: number;
  upperBoundSiteWorks: number;
  upperBoundTotal: number;
  gstStatus: "unverified" | "inclusive" | "exclusive";
  escalationStatus: "included" | "excluded" | "unverified";
  allocationMethod: "scenario_gfa_pro_rata";
}

export interface DeveloperCostAllowances {
  contingency?: CostAllowanceInput;
  professionalFees?: CostAllowanceInput;
  developmentFee?: CostAllowanceInput;
  consentsAmount?: number;
}

export interface Assumptions {
  projectName: string;
  operatorName: string;
  campusName: string;
  address: string;
  leaseTermYears: number;
  modelYears: number;
  purchasePrice: number;
  fullSitePrice: number;
  campusParcelAllocationLow: number;
  campusParcelAllocationHigh: number;
  stampDutyRate: number;
  acquisitionFeeRate: number;
  consultantCostRate: number;
  statutoryCostRate: number;
  planningApprovalCosts: number;
  developerContingencyRate: number;
  agentsFeeRate: number;
  devManagementFeeRate: number;
  developerCostBasis?: LoadedDeveloperCostBasis;
  developerCostAllowances?: DeveloperCostAllowances;
  otherDdCosts: number;
  tenantRelocationCosts: number;
  developmentMonths: number;
  holdingInterestRate: number;
  developerHurdleIRR: number;
  capRate: number;
  reddamFitoutAllowance: number;
  fitoutAmortisationYears: number;
  reddamCapexContribution: number; // tenant contribution toward the developer's capex/hard costs — funding
  // source: replaces otherwise-required developer debt without reducing gross
  // pre-finance TDC or developer equity; operator capex is amortised over the lease.
  contributionTiming?: ContributionTiming;
  rentIndexationPct: number;
  feePerStudentYear1: number;
  earlyLearningFeeYear1: number;
  primaryFeeYear1: number;
  secondaryFeeYear1: number;
  seniorFeeYear1: number;
  earlyLearningShare: number;
  primaryShare: number;
  secondaryShare: number;
  seniorShare: number;
  tuitionGrowthPct: number;
  foundingFamilyDiscount: number;
  siblingDiscount: number;
  discountDecayYears: number;
  rampYears: number;
  startingUtilisation: number;
  steadyStateUtilisation: number;
  targetEnrolment?: number; // >0 overrides building-derived capacity in enrollment calc
  enrolmentCaptureRate?: number; // 0.15 — share of catchment school-age children the campus
  //         captures; drives the catchment enrolment-pool estimate
  variableCostPerStudent: number;
  fixedOpex: number;
  fixedOpexGrowth: number;
  sportsFieldAreaSqm: number;
  sportsFieldIncomePerSqm: number; // operator's hire income per m² (stays operator revenue)
  sportsFieldRentPerSqm: number; // landlord rent per m² the operator pays the developer (→ NOI)
  sportsFieldConstructionPerSqm: number; // developer build cost per m² (civils/drainage/turf/fencing/lighting)
  parkingBays: number;
  parkingIncomePerBay: number;
  parkingRentPerBay: number; // landlord rent per bay/yr the operator pays the developer (→ NOI)
  parkingConstructionCostPerBay: number; // developer build cost per bay (surface vs structured)
  // Annual lump-sum ground rent the operator pays the developer (→ NOI); not per sqm.
  coveredPlayAreaGroundRent: number;
  // Developer build cost for covered play areas (→ TDC). No operator hire income.
  coveredPlayAreaConstructionCost: number;
  ancillaryGrowthPct: number; // annual escalation of operator hire/sublet income (default = CPI)
  weekdayParentParkingBays: number;
  weekdayParentParkingFee: number;
  weekdayParentParkingDays: number;
  weekendSportsParkingBays: number;
  weekendSportsParkingFee: number;
  weekendSportsParkingDays: number;
  staffReservedParkingBays: number;
  discountRate: number;
  corporateTaxRate: number;
  minimumCashReserveMonths: number;
  // ---- Debt & finance ----
  constructionLvrRate: number; // 0.70  — facility / total dev cost
  constructionMarginRate: number; // 0.017 — interest margin p.a. on drawn balance
  constructionLineFeeRate: number; // 0.017 — line fee p.a. on facility limit
  termLoanLvrRate: number; // 0.60  — term loan / gross realisation at completion
  termLoanMarginRate: number; // 0.016 — term loan margin p.a.
  termLoanHoldYears: number; // 5     — years held on term loan before exit sale
  useScurveDrawdown: boolean; // true  — monthly S-curve vs 50% avg draw
  // ---- equity return modelling (Q4 v2 review — toggle, default perpetuity) ----
  equityReturnFormulation?: "perpetuity" | "amortised";
  // ---- development management fee base inclusions (default: dev cost only; land + all finance EXCLUDED) ----
  // The 4% DM fee is always charged on development/construction costs. These optionally add cost
  // categories into the fee base. All default false (undefined → excluded). Including line fee /
  // construction interest charges the fee on finance the developer borrows — uncommon and generally unfavourable to the operator.
  dmFeeIncludeLand?: boolean; // include land acquisition (purchase + stamp + acq fee)
  dmFeeIncludeHoldingInterest?: boolean; // include land/DD holding interest (carry over the build)
  dmFeeIncludeLineFee?: boolean; // include construction facility line fee
  dmFeeIncludeConstructionInterest?: boolean; // include construction facility interest
  // ---- builder additions on hard construction (net-trade → construction TPC) ----
  preliminariesRate: number; // 0.145 — preliminaries as ratio of hard construction (ACT BGA ~14.5%)
  builderMarginRate: number; // 0.05  — builder margin/overhead as ratio of hard construction
  schoolSpecTier: SchoolSpecTier; // 'private_mid' — tier for the data-driven construction $/m² benchmark
  // ---- risk & contract terms (v2) ----
  performanceBondPct: number; // 0.10  — ratio of contract sum
  defectsLiabilityMonths: number; // 24    — months
  bankGuaranteeMonths: number; // 6     — months of GROSS RENT (lease security). 6–12 common for
  //         special-purpose BTS (cash- or facility-backed). Lower favours
  //         the tenant; distinct from minimumCashReserveMonths (opex reserve).
  externalWorksPct: number; // 0.15  — ratio of TPC
  // ---- statutory approval costs + split (Phase G) ----
  ssdaCost?: number; // NSW State Significant Development Application
  changeOfUseCost?: number; // Change-of-use approval (EP&A Act + SEPP Education 2023)
  otherApprovalCosts?: number; // BAL cert, ESD report, traffic study, etc.
  approvalCostReddamSharePct?: number; // 0..1; default 0.5 per pre-agreed deal terms
  // ---- site-deal context (Phase H) ----
  // ---- rent escalation (S3.1) ----
  rentEscalationMethod?: "fixed" | "cpi" | "market";
  cpiAssumption?: number;
  marketReviewPct?: number;

  // ---- developer exit (S3.2) ----
  developerExitYear?: number;

  siteDealAllocationStatus?: "unverified" | "negotiating" | "agreed";
  // ---- per-scenario market picker target (FOUNDATION: CENSUS seam) ----
  catchmentCode?: string; // registry area_code; resolver precedence code→blob→default

  // ---- lease incentive machinery (W2) ----
  // Norm: effective rent = face rent − amortised incentive per ISCA / Savills methodology
  leaseIncentiveLumpSum: number; // $ landlord contribution at commencement (default: 0)
  rentFreeMonths: number; // months rent-free at start (default: 0)
  incentiveAmortisationYears: number; // spread period for affordability analysis (default: leaseTermYears)

  // ---- lease-vs-own comparison (W3) ----
  // Norm: school WACC = RBA cash rate + education borrowing spread (150–250 bps); default = discountRate
  schoolWacc: number; // school cost of capital for own-path NPV (default: 0.085)
}

export interface Building {
  id: string;
  name: string;
  nlaSqm: number;
  gfaSqm: number;
  constructionPerSqmGfa: number;
  reddamCapex: number;
  netRentPerSqm: number;
  outgoingsPerSqm: number;
  enrolmentCapacity: number;
  comesOnlineYear: number;
  // ---- per-building construction phasing (E.1) ----
  constructionStartMonth?: number; // Month construction starts (0 = project start); defaults to 0
  constructionMonths?: number; // Build period for this building; defaults to assumptions.developmentMonths
  // ---- per-building risk overrides (Phase F) ----
  balRating?: "LOW" | "BAL-12.5" | "BAL-19" | "BAL-29" | "BAL-40" | "unknown";
  balCostOverride?: number; // user can override the seed cost band
  hazmatExposurePerSqm?: number; // $/sqm seed; user can edit
  hazmatStatus?:
    "unsurveyed" | "surveyed-clean" | "surveyed-contaminated" | "remediated";
}

export interface Campus {
  totalCapacity: number;
  totalEnrolmentCapacity: number;
  totalNlaSqm: number;
  totalGfaSqm: number;
  totalReddamCapex: number;
  totalYear1NetRent: number;
  totalYear1Outgoings: number;
  blendedFeeYear1: number;
}

export interface Year {
  year: number;
  enrolment: number;
  utilisation: number; // enrolment ÷ capacity BASIS (ramp target) — ≤ steadyStateUtilisation ≤ 1 (P6-R2)
  seatPressure: number; // enrolment ÷ physically LIVE seats — >1 = oversubscribed (P6-R2 / #8)
  tuitionRevenue: number;
  headlineFee: number;
  effectiveFee: number;
  ancillaryRevenue: number;
  totalRevenue: number;
  rentNet: number;
  outgoings: number;
  rentGross: number; // BUILDING face rent (net + outgoings) — amenity-free; the rent-bridge basis
  amenityRent: number; // sports + parking LL rent the operator pays (separate lease cost) — P4-R2
  variableCosts: number;
  fixedOpex: number;
  fitoutAmortisation: number;
  tax: number;
  reddamEbitda: number; // TRUE EBITDA — excludes D&A (fit-out amortisation)
  reddamNetIncome: number; // accrual net income = EBITDA − amortisation − tax
  reddamOperatingCashflow: number; // after-tax operating cash = EBITDA − tax (non-cash amort added back)
  cumulativeCashflow: number;
  rentPctRevenue: number;
}

export interface Developer {
  purchasePrice: number;
  stampDuty: number;
  acquisitionFee: number;
  hardConstruction: number;
  siteWorksConstruction: number;
  loadedConstructionTotal: number;
  costBasisMode: "legacy_net_trade" | "loaded_aggregate";
  preliminaries: number;
  builderMargin: number;
  consultantCosts: number;
  statutoryCosts: number;
  planningApprovalCosts: number;
  constructionContingency: number;
  holdingInterest: number;
  developmentManagementFee: number;
  dmFeeBase: number; // the cost base the DM fee % was charged on (after inclusions)
  otherDdCosts: number;
  tenantRelocationCosts: number;
  totalCost: number;
  allInCost: number; // pre-finance totalCost + construction interest + line fee
  netRentYear1: number;
  noi: number;
  grossRealisation: number;
  sellingCosts: number;
  netRealisation: number;
  profit: number;
  marginOnCost: number;
  irr: number;
  hurdleMet: boolean;
  hurdleStatus: "met" | "marginal" | "below";
  sportsIncome: number; // operator hire income on the sports field (informational; NOT in NOI)
  parkingIncome: number; // operator parking sublet income (informational; NOT in NOI)
  sportsFieldRent: number; // landlord rent on the sports field (in NOI; operator pays it)
  parkingRent: number; // landlord rent on parking (in NOI; operator pays it)
  // Annual lump-sum ground rent on covered play areas (in NOI; operator pays it).
  coveredPlayAreaRent: number;
  amenityConstruction: number; // developer build cost for sports field + parking + covered play areas (in totalCost/TDC)
  yieldOnCost: number; // NOI / total development cost
  // ---- geared (debt model) ----
  grossFacilityLimitDev: number; // lender capacity before operator contribution
  facilityLimitDev: number; // actual debt remaining after contribution
  peakDebtDev: number; // maximum construction debt before a completion contribution
  operatorContributionApplied: number;
  equityRequired: number; // totalCost − grossFacilityLimitDev
  fundingResidual: number; // equity + actual debt + contribution − totalCost
  debtCostDev: number; // construction interest + line fee
  constructionInterest: number;
  lineFee: number; // one-off facility establishment fee
  termLoanLimit: number; // term loan at completion
  termLoanAnnualService: number; // interest on term loan per year
  gearedDevelopmentIrr: number; // equity IRR at dev-exit (sell at PC)
  ungearedEarlyExitIrr: number; // project IRR holding to developerExitYear; NaN when exit at horizon
  gearedEarlyExitIrr: number; // equity IRR holding to developerExitYear (early flip); NaN when developerExitYear >= modelYears
  gearedWholeOfLifeIrr: number; // equity IRR holding through term loan
  gearedHurdleMet: boolean;
  gearedHurdleStatus: "met" | "marginal" | "below" | "na";
  yieldOnCash: number; // NOI / equity
  moneyMultiple: number; // total equity distributions of the selected strategy ÷ equity (true equity multiple, P5-R2): sell-at-PC = (PC net realisation − facility − construction debt costs); early exit = PC refinance leg + interim net rent after term-loan service + exit sale net of the term loan
  developerApprovalShare: number; // Phase G: developer's share of named approval costs
  // ---- cashflow vectors for Excel IRR formulas ----
  cashflowUngeared: number[]; // monthly developer cashflow (ungeared)
  cashflowUngearedEarlyExit: number[]; // monthly developer cashflow (ungeared, holding to developerExitYear; empty when exit at horizon)
  cashflowGearedDevExit: number[]; // monthly developer cashflow (geared, dev-exit, sell-at-PC)
  cashflowGearedEarlyExit: number[]; // monthly developer cashflow (geared, holding to developerExitYear; empty when exit at horizon)
  cashflowGearedWholeOfLife: number[]; // monthly developer cashflow (geared, whole-of-life)
  reddamApprovalShare: number; // Phase G: Reddam's share of named approval costs (in opCapex)
  lastCompletionMonth: number; // E.1: month when last building completes construction
  gearedHurdle: number; // cashflow-equivalent geared reference at the matched-horizon ungeared hurdle; NaN when unavailable
  gearedHurdleConverged: boolean;
  dscr: number; // NOI / termLoanAnnualService (0 for unlevered deals)
  dscrStatus: "good" | "warn" | "bad" | "na"; // 'na' for unlevered scenarios (no covenant obligation)
  leaseIncentiveCost: number; // W2: PV of incentive package borne by developer at commencement
}

export interface KPIs {
  reddamNpv: number;
  reddamIrr: number | null;
  paybackYear: number | null;
  cumulativeCashflowYearN: number;
  avgEbitda: number;
  yearOneEbitda: number;
  rentPctRevenueYear1: number;
  rentPctRevenueAvg: number;
  rentPctRevenueSteadyState: number; // P1-R1 — final-modelled-year occupancy-cost ratio (rent ÷ revenue tail)
  breakEvenRentY1: number; // P1-R2 — Y1 gross rent ($) where reddamEbitda(Y1) = 0
  breakEvenEnrolment: number; // P1-R3 — steady-state enrolment where reddamEbitda = 0
  breakEvenRentConverged: boolean; // P1 hardening — false ⇒ breakEvenRentY1 is a fallback (no EBITDA-0 crossing); render a caveat
  breakEvenEnrolmentConverged: boolean; // P1 hardening — false ⇒ breakEvenEnrolment is a fallback (no EBITDA-0 crossing); render a caveat
  developerIrr: number;
  developerUngearedEarlyExitIrr: number; // ungeared IRR at developerExitYear; NaN when exit at horizon
  developerMarginOnCost: number;
  developerYieldOnCost: number; // NOI / total development cost
  developerHurdleMet: boolean;
  developerHurdleStatus: "met" | "marginal" | "below";
  developerGearedIrr: number; // geared dev-exit IRR (sell-at-PC)
  developerGearedEarlyExitIrr: number; // geared IRR at developerExitYear (early flip); NaN when developerExitYear >= modelYears
  developerGearedWholeOfLifeIrr: number; // geared hold-through IRR
  developerGearedHurdleMet: boolean;
  developerGearedHurdleStatus: "met" | "marginal" | "below" | "na";
  developerPrimaryGearedIrr: number; // geared IRR on the developer's actual horizon: early-exit when developerExitYear < modelYears, else sell-at-PC (headline metric)
  developerPrimaryIrr: number; // ungeared IRR on the developer's actual horizon: early-exit when configured, else sell-at-PC
  developerPrimaryHurdleMet: boolean; // matched-horizon ungeared project IRR versus the stated developerHurdleIRR
  developerPrimaryHurdleStatus: "met" | "marginal" | "below";
  developerYieldOnCash: number; // NOI / equity
  developerNpv: number;
  developerEquityRequired: number;
  developerMoneyMultiple: number; // mirrors developer.moneyMultiple (total equity distributions of the selected strategy ÷ equity)
  steadyStateRevenue: number;
  totalTax: number;
  // Ramp survivability (W5) — derived from years[]; no new solver, no new assumptions
  // Norm: peak drawdown = minimum cumulative cashflow (incl. capex outlay); reserves = -drawdown if negative
  negativeEbitdaYears: number; // count of modelled years where operator EBITDA < 0
  maxCumulativeDrawdown: number; // min(years[].cumulativeCashflow) — deepest operator cash trough (negative = drawdown)
  reservesRequiredToSurvive: number; // max(0, -maxCumulativeDrawdown) — cash reserves the operator must hold to survive ramp
  // Lease incentive (W2) — face vs effective rent; zero when leaseIncentiveLumpSum=0 and rentFreeMonths=0
  // Norm: effective rent = face rent − amortised incentive per ISCA / Savills methodology
  leaseIncentivePvTotal: number; // NPV of full incentive package (developer cost at T=0)
  incentiveAmortisedPerYear: number; // leaseIncentivePvTotal / incentiveAmortisationYears
  effectiveRentY1: number; // face BUILDING rentGross_y1 − incentiveAmortisedPerYear — building-rent basis, EXCLUDES amenity rent
  effectiveRentPctRevenueY1: number; // effectiveRentY1 / totalRevenue_y1 — NOT an occupancy-cost ratio; never grade against RENT_PCT_REVENUE_*
  // Amenity-inclusive effective occupancy cost — the only effective ratio comparable to rentPctRevenueYear1
  // (same basis: building rent + outgoings + amenity landlord rent) and therefore the only one graded against
  // RENT_PCT_REVENUE_THRESHOLD / RENT_PCT_REVENUE_CAUTION.
  effectiveOccupancyCostY1: number; // (rentGross_y1 + amenityRent_y1) − incentiveAmortisedPerYear
  effectiveOccupancyCostPctRevenueY1: number; // effectiveOccupancyCostY1 / totalRevenue_y1 (= rentPctRevenueYear1 when incentive is zero)
}

export type UnitType =
  | "currency"
  | "percentage"
  | "count"
  | "area"
  | "months"
  | "years"
  | "rate"
  | "text";

export interface Flag {
  level: "error" | "warning" | "info";
  message: string;
}

export interface Month {
  month: number;
  year: number;
  phase: "development" | "ramp" | "steady";
  label: string;
  developerOutflow: number;
  developerInflow: number;
  reddamOutflow: number;
  reddamRevenue: number;
  reddamRent: number;
  reddamOpex: number;
  reddamNetIncome: number;
  cumulativeCashflow: number;
  enrolment?: number;
  utilisation?: number;
  notes?: string;
}

export interface MonthlyResult {
  months: Month[];
  developmentMonths: number;
  year1Months: number;
}

export interface RentDecompositionComponent {
  key:
    | "construction_debt_service"
    | "equity_return"
    | "developer_margin"
    | "land_recovery"
    | "soft_costs"
    | "opex_recovery"
    | "approval_recovery";
  label: string;
  scenarioAmount: number; // annualised under scenario's actual netRentPerSqm
  calibratedAmount: number; // annualised under rent calibrated for hurdle IRR
  gapAmount: number; // calibrated - scenario (positive = under-paying)
  scenarioShareOfRent: number;
  calibratedShareOfRent: number;
}

export interface RentDecomposition {
  scenarioRentY1: number;
  calibratedRentY1: number;
  gapRentY1: number; // calibrated - scenario, in $/yr
  scenarioLifetimeNpv: number;
  calibratedLifetimeNpv: number;
  components: RentDecompositionComponent[];
  rentTargetMetric:
    | "developerGearedIrr"
    | "developerGearedEarlyExitIrr"
    | "developerGearedWholeOfLifeIrr"
    | "developerYieldOnCost"
    | "developerMarginOnCost";
  calibratedSolverConverged: boolean;
}

export interface RentBuildUpComponent {
  key:
    | "land_recovery"
    | "construction_debt_service"
    | "soft_costs"
    | "approval_recovery"
    | "opex_recovery"
    | "equity_return";
  label: string;
  annualAmount: number; // UNSCALED annualised cost basis ($/yr)
}

export interface RentBuildUp {
  components: RentBuildUpComponent[]; // 6 cost lines (NO margin line — margin is the residual)
  costRecoveryRent: number; // Σ components — developer's hurdle break-even rent (live build-up)
  scenarioRentY1: number; // the developer ASK (Y1 gross rent)
  marginOverreach: number; // scenarioRentY1 − costRecoveryRent (residual; > 0 = over-asking)
  calibratedRentY1: number; // exact solver-derived hurdle floor (reconciliation reference)
  calibratedSolverConverged: boolean;
}

export interface RentMultiplierResult {
  baselineTpc: number;
  baselineRentY1: number;
  deltaTpc: number; // nominal HARD-construction bump applied (display unit, $1M)
  bumpedRentY1: number;
  perDollarTpc: number; // rent uplift per $1 of REALISED TPC delta (P5-R3; was per nominal hard $)
  lifetimeRentDelta: number; // per-$1M-realised-TPC Y1 uplift held flat over the term
  lifetimeRentDeltaNpv: number; // NPV of the per-$1M-realised-TPC uplift (grown + discounted)
  targetMetric:
    | "developerGearedIrr"
    | "developerGearedEarlyExitIrr"
    | "developerGearedWholeOfLifeIrr"
    | "developerYieldOnCost"
    | "developerMarginOnCost";
}

export interface TpcSweepRow {
  tpc: number;
  tpcPctOfBase: number;
  developerIrr: number;
  developerGearedIrr: number;
  developerGearedWholeOfLifeIrr: number;
  developerMarginOnCost: number;
  developerYieldOnCost: number;
  hurdleMet: boolean | null; // null when the baseline reference or row IRR is unavailable
  hurdleIrr: number;
}

export interface TpcSweepResult {
  baselineTpc: number;
  baselineIrr: number;
  hurdleIrr: number;
  cliffTpc: number | null;
  cliffPctOverBase: number | null;
  rows: TpcSweepRow[];
}

/** W3 — Lease-vs-Own illustrative NPV comparison.
 *  Uses schoolWacc (school cost of capital) to discount lease rent outflows
 *  against the NPV of owning (capex outlay, terminal value recovery).
 *  ALL outputs are illustrative — see mandatory caveats array. */
export interface LeaseVsOwnResult {
  leasePvAtWacc: number; // PV of rent outflows discounted at schoolWacc (negative)
  ownPathCapex: number; // developer.totalCost (simplified: paid at Y0)
  ownPathTerminalValue: number; // developer.netRealisation (NOI/capRate exit value, net of selling costs)
  ownPathTerminalValuePv: number; // PV of terminal value at schoolWacc
  ownPathNpv: number; // -capex + terminalValuePv
  deltaOwningVsLeasing: number; // ownPathNpv − leasePvAtWacc (+ve = owning cheaper)
  impliedBreakEvenCapex: number; // capex where delta = 0 (leasePv + terminalValuePv)
  schoolWacc: number; // rate used (shown in output)
  caveats: string[]; // mandatory disclosures (always emitted)
}

export interface BuilderQuoteExclusion {
  itemKey: string;
  label: string;
  status: "included" | "excluded" | "nil" | "silent";
  dollarAmount?: number;
  notes?: string;
}

export interface BuilderQuoteScope {
  itemKey: string;
  label: string;
  dollarAmount: number;
  per?: "building" | "project";
  buildingId?: string;
}

export interface BuilderQuote {
  id: string;
  builderName: string;
  bidDate: string; // ISO yyyy-mm-dd
  reportedTpc: number;
  excludedDesignFees?: boolean;
  excludedContingencyPct?: number; // contingency % the builder excluded (we'll add back for LFL)
  excludedDesignFeesPct?: number; // typically 0.045
  scope: BuilderQuoteScope[]; // optional itemised scope (e.g. "Sports Hall = $59M")
  exclusions: BuilderQuoteExclusion[];
  scoreDocumentationDepth?: BuilderQuoteScore; // optional for legacy imported quotes
  notes?: string;
}

export interface BuilderQuoteScore {
  D1_tradeDetail: 0 | 1 | 2 | 3 | 4 | 5;
  D2_quantitiesRates: 0 | 1 | 2 | 3 | 4 | 5;
  D3_scheduleAlignment: 0 | 1 | 2 | 3 | 4 | 5;
  D4_exclusionsCoverage: 0 | 1 | 2 | 3 | 4 | 5; // re-scored per panel: coverage not structure
  D5_sourceVerification: 0 | 1 | 2 | 3 | 4 | 5;
  D7_rateAlignment: 0 | 1 | 2 | 3 | 4 | 5; // NEW (panel): vs published benchmarks
  D8_contingencyTransparency: 0 | 1 | 2 | 3 | 4 | 5; // NEW (panel)
  // D6 (program) intentionally dropped per panel finding
  preliminaryFlag?: "no_rlb_benchmark" | "no_audit" | null;
}

export interface BuilderQuoteAnalysis {
  quotes: BuilderQuote[];
  lflTpcs: { quoteId: string; lflTpc: number }[];
  medianLflTpc: number;
  aaceBandLow: number;
  aaceBandHigh: number;
  aaceClass: 1 | 2 | 3 | 4 | 5;
  weightedMeanLflTpc: number; // sanity check vs median
  weightedMeanDivergesFromMedian: boolean; // true if >10% apart
  divergentExclusions: DivergentExclusion[];
  separableLevers: {
    label: string;
    swingDollars: number;
    phasingScore: 1 | 2 | 3 | 4 | 5;
  }[];
}

export interface DivergentExclusion {
  itemKey: string;
  label: string;
  status: Record<string, "included" | "excluded" | "nil" | "silent">; // builderId → status
  severity: "low" | "med" | "high" | "critical";
  notes?: string;
}

export interface ComplianceThreshold {
  key: string;
  label: string;
  source: string; // doc + section, e.g. "NorthRyde_Comps row 26"
  category: "lease" | "cost" | "capital" | "risk" | "planning";
  type: "range" | "maxRatio" | "minValue" | "maxValue" | "enum";
  low?: number;
  mid?: number;
  high?: number;
  unit?: string;
  blocking: boolean; // if false → renders as info badge
  notes?: string;
}

export interface ComplianceCheck {
  id: string;
  label: string;
  actualValue: number | string;
  threshold: ComplianceThreshold;
  status: "pass" | "warn" | "info" | "fail";
  tooltip: string;
}

export interface ActionItem {
  id: string;
  severity: "low" | "med" | "high" | "critical";
  title: string;
  description: string;
  estimatedCostBandLow: number;
  estimatedCostBandHigh: number;
  affectsBuildings?: string[]; // building IDs (per-building items)
  status: "open" | "in-review" | "resolved" | "accepted";
  source?: string;
}

export interface ModelResult {
  years: Year[];
  developer: Developer;
  campus: Campus;
  kpis: KPIs;
  flags: Flag[];
}

export interface AuditEntry {
  field: string;
  oldValue: unknown;
  newValue: unknown;
  timestamp: number;
  userId?: string;
}

export interface ReviewedInputResolution {
  originalNote: string;
  resolutionNote: string;
  timestamp: number;
}

export interface Scenario {
  id: string;
  name: string;
  description: string;
  assumptions: Assumptions;
  buildings: Building[];
  builderQuotes?: BuilderQuote[];
  actionItems?: ActionItem[];
  auditLog?: AuditEntry[];
  // ── platform-foundation additions (ALL optional) ──
  siteId?: string; // MULTI-SITE grouping (NULL/undefined = ungrouped)
  catchment?: CensusArea; // CENSUS per-scenario market SNAPSHOT (undefined = resolve via code/default)
  updatedAt?: number; // epoch ms; fixes the conflict-resolver bug (§4)
  schemaVersion?: number; // blob schema version (undefined ⇒ treat as 1) (§5)
  calculationMethod?: "legacy-v1" | "dated-v2";
  datedFinance?: DatedFinanceInput;
  datedOwnership?: OwnershipInput;
  comparisonPresets?: ComparisonPreset[];
  sourceScenarioId?: string;
  unresolvedInputNotes?: string[];
  reviewedInputResolutions?: ReviewedInputResolution[];
}

export interface CheckResult {
  id: string;
  area: string;
  level: "pass" | "warn" | "fail";
  label: string;
  detail: string;
}

export interface Benchmark {
  metric: string;
  unit: string;
  low: number;
  mid: number;
  high: number;
  source: string;
}

export interface Driver {
  key: string;
  label: string;
  range: number;
  apply: (scenario: Scenario, delta: number) => void;
}

export interface TornadoRow {
  variable: string;
  label: string;
  low: number;
  high: number;
  base: number;
}

export interface CustomBenchmark {
  metric: string;
  low: number;
  mid: number;
  high: number;
  unit: string;
}

export interface ScenarioContextValue {
  scenarios: Scenario[];
  activeId: string;
  active: Scenario;
  result: ModelResult;
  results: Record<string, ModelResult>;
  setActiveId: (id: string) => void;
  updateAssumption: (
    key: keyof Assumptions,
    value: number | string | boolean,
  ) => void;
  updateBuilding: (id: string, patch: Partial<Building>) => void;
  addBuilding: () => void;
  removeBuilding: (id: string) => void;
  resetActiveToDefault: () => void;
  cloneActiveAs: (name: string) => void;
  deleteScenario: (id: string) => void;
  exportScenario: () => string;
  importScenario: (json: string) => void;
  benchmarkBuilding: Building | null;
  saveBenchmarkBuilding: (id: string) => void;
  saveCustomBenchmarkBuilding: (b: Omit<Building, "id">) => void;
  clearBenchmarkBuilding: () => void;
  addBuildingFromBenchmark: () => void;
  loading: boolean;
  mcResult: import("./model").MonteCarloResult | null;
  setMcResult: (result: import("./model").MonteCarloResult | null) => void;
}

export interface KPICardProps {
  eyebrow: React.ReactNode;
  value: React.ReactNode;
  hint?: React.ReactNode;
  foot?: React.ReactNode;
  tooltip?: string;
  accent?: "primary" | "glow";
  size?: "sm" | "lg" | "xl";
  spark?: React.ReactNode;
  status?: "good" | "bad" | "warn" | "info" | "na";
  className?: string;
}

export interface SectionProps {
  eyebrow?: string;
  title?: string;
  lede?: string;
  right?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}

export interface FlagItemProps {
  level: "error" | "warning" | "info";
  message: string;
}

// Chart types
export interface ChartPadding {
  l?: number;
  r?: number;
  t?: number;
  b?: number;
}

export interface LineAreaProps {
  data: { x: number; y: number }[];
  width?: number;
  height?: number;
  accent?: string;
  label?: string;
  yFormat?: (v: number) => string;
  padding?: ChartPadding;
  showZero?: boolean;
  overlay?: {
    data: { x: number; y: number }[];
    color?: string;
    dasharray?: string;
    label?: string;
  }[];
}

export interface BarSeriesItem {
  key: string;
  label: string;
  color: string;
}

export interface BarSeriesOverlay {
  key: string;
  label: string;
  data: number[];
  color: string;
  yFormat?: (v: number) => string;
}

export interface BarSeriesProps {
  data: Array<{ label: string } & Record<string, number | string>>;
  series: BarSeriesItem[];
  width?: number;
  height?: number;
  yFormat?: (v: number) => string;
  showLegend?: boolean;
  overlays?: BarSeriesOverlay[];
}

export interface WaterfallItem {
  label: string;
  value: number;
}

export interface WaterfallProps {
  items: WaterfallItem[];
  total?: number;
  proceeds?: number;
  width?: number;
  height?: number;
  valueFormat?: (v: number) => string;
}

export interface TornadoProps {
  rows: Array<{ variable: string; label: string; low: number; high: number }>;
  base: number;
  width?: number;
  rowHeight?: number;
  valueFormat?: (v: number) => string;
  label?: string;
}

export interface GaugeRingProps {
  value: number;
  target?: number;
  size?: number;
  label?: string;
  thickness?: number;
  valueFormat?: (v: number) => string;
}

export interface SparklineProps {
  values: number[];
  accent?: string;
  height?: number;
  valueFormat?: (v: number) => string;
}

export interface DonutProps {
  parts: Array<{ label: string; value: number; color: string }>;
  size?: number;
  thickness?: number;
  total?: number;
  valueFormat?: (v: number) => string;
}

export interface CashflowChartProps {
  years: Year[];
  openingBalance?: number;
  width?: number;
  height?: number;
  yFormat?: (v: number) => string;
  paybackYear?: number | null;
  operator?: string;
}

export interface StarsBackgroundProps {
  count?: number;
  seed?: number;
}

/* ------------------------------------------------------------------ */
//  Re-exports (platform coordination)
/* ------------------------------------------------------------------ */

export type { CensusArea } from "./census";
